/**
 * «Наши фото»: типы ответов `/api/photos`, `/api/dresses/*` и обёртки ручек
 * правки папок (reels/web/api_photos.py поверх reels/photos.py).
 *
 * Папка-платье — автокластер или ручная папка; имя даёт человек, поэтому у
 * безымянной папки на экране «Платье #id». Фото — одно на хэш байтов, а
 * упоминаний (сообщений Telegram с ним) может быть несколько.
 *
 * Формы — по `photos.dresses`/`photos.dress`: даты — ISO без зоны, как везде.
 */
import { apiFetch } from "@/lib/api";
import { matchQuery, type CityMode, type MatchResult } from "@/lib/crmMatch";
import type { DressSimilar, SceneRef } from "@/lib/dressSimilar";
import type { CrmPhoto } from "@/lib/crmImport";
import type { DressDeletePreview } from "@/lib/dressDelete";
import { post, type CollectTask, type Pin } from "@/lib/trends";

export type Sender = { user_id: number; name: string };

export type Cover = { photo_id: number; thumb_path: string | null } | null;

export type DressCard = {
  id: number;
  name: string | null;
  count: number;
  cover: Cover;
  senders: Sender[];
  regions: string[];
  date_first: string | null;
  date_last: string | null;
  /** В папке есть фото, импортированные из CRM. */
  source_crm: boolean;
};

/** Файл из чата, который не лёг в справочник: больше 20 МБ или не скачался. */
export type FailedMention = {
  chat_tg_id: number;
  message_tg_id: number;
  user_name: string;
  file_name: string | null;
  state: "too_big" | "failed";
  error: string | null;
};

export type PhotosList = {
  dresses: DressCard[];
  senders: (Sender & { photos: number })[];
  regions: { short: string; photos: number }[];
  failed: FailedMention[];
};

export type Mention = {
  user_id: number;
  user_name: string;
  location_short: string | null;
  date: string;
  file_name: string | null;
  message_url: string;
};

export type Photo = {
  id: number;
  asset_path: string;
  /** JPG для просмотра; NULL — конвертация не удалась (причина в `error`). */
  view_path: string | null;
  thumb_path: string | null;
  width: number | null;
  height: number | null;
  /** `manual` — папку поставил человек, кластеризация её не тронет. */
  link_kind: "auto" | "manual";
  error: string | null;
  /** id фото в CRM (`sCatalog_model_media.id`); NULL — фото не из CRM. */
  crm_media_id: number | null;
  mentions: Mention[];
};

export type Dress = {
  id: number;
  name: string | null;
  note: string | null;
  cover_photo_id: number | null;
  photos: Photo[];
};

export type MoveResult = { dress_id: number; created?: boolean; moved: number; deleted_dresses: number[] };

/** Ключи кэша react-query: после любой правки сбрасываются и папка, и список. */
export const photoKeys = {
  list: ["photos"] as const,
  dress: (id: number) => ["photos", "dress", id] as const,
  pins: (id: number) => ["photos", "dress", id, "pins"] as const,
  crm: (id: number) => ["photos", "dress", id, "crm"] as const,
  crmMatch: (id: number, city: string | null) => ["photos", "dress", id, "crm-match", city] as const,
  crmImport: (modelId: number) => ["photos", "crm-import", modelId] as const,
};

export const loadPhotos = () => apiFetch<PhotosList>("/api/photos");
export const loadDress = (id: number) => apiFetch<Dress>(`/api/dresses/${id}`);
/** Платья по сходству со сценой (референс или пин), лучшие первыми. */
export const searchDressesByScene = (s: SceneRef) => apiFetch<DressSimilar>(`/api/photos/similar?${s.kind}=${s.id}`);

/** Пины Pinterest, найденные по фото платья (Lens), и задачи поиска этого платья. */
export type DressPins = { pins: Pin[]; searches: unknown[]; tasks: CollectTask[] };

export const loadDressPins = (id: number) => apiFetch<DressPins>(`/api/dresses/${id}/pins`);
/** Без `photoId` — по обложке. Повтор при активной задаче вернёт её же. */
export const startLens = (dressId: number, photoId?: number) =>
  post<{ task_id: number }>(`/api/dresses/${dressId}/lens`, photoId === undefined ? {} : { photo_id: photoId });

export const renameDress = (id: number, name: string) =>
  post<{ id: number; name: string | null; pinned: number }>(`/api/dresses/${id}/rename`, { name });
export const setCover = (id: number, photoId: number) =>
  post<{ id: number; cover_photo_id: number }>(`/api/dresses/${id}/cover`, { photo_id: photoId });
export const mergeDresses = (dressIds: number[], into: number) =>
  post<MoveResult>("/api/dresses/merge", { dress_ids: dressIds, into });
/** `dressId` null — в новую папку. */
export const movePhotos = (photoIds: number[], dressId: number | null) =>
  post<MoveResult>("/api/photos/move", { photo_ids: photoIds, dress_id: dressId });
export const resetAuto = (photoIds: number[]) => post<{ reset: number }>("/api/photos/auto", { photo_ids: photoIds });

/** Предложение модели CRM: серия — самая частая в наличии, цена — минимум аренды; нет в наличии — цены нет. */
export type CrmOffer = { model_id: number; name: string | null; series: string | null; price_from: number | null; serials: number };

/** Привязка платья к модели CRM: `series` — серия, по которой привязали. */
export type DressCrm = { crm_model_id: number | null; series: string | null; offer: CrmOffer | null; crm_error: string | null };

export type CrmBind =
  | { status: "bound"; offer: CrmOffer }
  | { status: "choose"; models: { model_id: number; name: string | null; serials: number }[] }
  | { status: "not_found" };

export const loadDressCrm = (id: number) => apiFetch<DressCrm>(`/api/dresses/${id}/crm`);
/** CRM недоступна — 503 `CRM_UNAVAILABLE`; `modelId` не из серии — 4xx. */
export const bindDressCrm = (id: number, series: string, modelId?: number) =>
  post<CrmBind>(`/api/dresses/${id}/crm`, modelId === undefined ? { series } : { series, model_id: modelId });
/** Модели CRM, похожие на фото папки; `city` — `null` (город фото), `"all"` или id города. */
export const loadDressCrmMatch = (id: number, city: CityMode) =>
  apiFetch<MatchResult>(`/api/dresses/${id}/crm/match${matchQuery(city)}`);
export const unbindDressCrm = (id: number) =>
  apiFetch<{ crm_model_id: null }>(`/api/dresses/${id}/crm`, { method: "DELETE" });

/** Удаление папки (№438): сводка и текст окна — `lib/dressDelete.ts`. */
export const loadDressDelete = (id: number) => apiFetch<DressDeletePreview>(`/api/dresses/${id}/delete`);
/** `excludeModel` — и не предлагать модель в «Импорт топ»; держат платье — 409 `DRESS_BUSY`. */
export const deleteDress = (id: number, excludeModel: boolean) =>
  apiFetch<{ deleted: number; photos: number; excluded_model: boolean }>(
    `/api/dresses/${id}?exclude_model=${excludeModel}`,
    { method: "DELETE" },
  );

/** Импорт фото платья из CRM: поиск модели, фото модели с оценками, запуск. */
export type CrmCandidate = { model_id: number; name: string | null; series: string | null; in_stock: number };
export type CrmModelData = {
  model: { id: number; name: string | null; series: string | null; in_stock: number; excluded: boolean };
  folders: { id: number; name: string | null; count: number }[];
  photos: CrmPhoto[];
  /** prokat недоступен — оценок нет, фото в группе «проверить». */
  score_error: string | null;
};
export type CrmImportResult = {
  dress_id: number | null;
  imported: number;
  excluded: number;
  failed: { media_id: number; error: string }[];
};

/** Серия либо модель (номер модели или её id) — одно из двух. */
export const lookupCrm = (q: { series: string } | { model: string }) => {
  const [key, value] = "series" in q ? (["series", q.series] as const) : (["model", q.model] as const);
  return apiFetch<{ candidates: CrmCandidate[] }>(`/api/crm-import/lookup?${key}=${encodeURIComponent(value)}`);
};
export type CrmType = { id: number; name: string };
export type CrmTopModel = {
  rank: number;
  model_id: number;
  name: string | null;
  series: string | null;
  deals: number;
  deals_last: number;
  folder_id: number | null;
  folders: { id: number; name: string | null; count: number }[];
  photos: CrmPhoto[];
};
/** Модель, исключённая из импорта топа; нет в CRM — name/series null. */
export type CrmExcludedModel = {
  model_id: number;
  name: string | null;
  series: string | null;
  user_id: number | null;
  user_name: string | null;
  date_create: string;
};
export type CrmTopData = {
  models: CrmTopModel[];
  excluded: CrmExcludedModel[];
  total_models: number;
  shown_models: number;
  new_photos: number;
  score_error: string | null;
  /** Моделей (по всем типам), из которых уже импортировали хоть одно фото: в топ они не идут (№426). */
  imported_models: number;
};
export const loadCrmTypes = () => apiFetch<{ types: CrmType[] }>("/api/crm-import/types");
/** Топ-N моделей по сделкам в выбранных типах платья (N 1..500). */
export const loadCrmTop = (types: number[], limit: number) =>
  apiFetch<CrmTopData>(`/api/crm-import/top?types=${types.join(",")}&limit=${limit}`);
export const loadCrmModel = (modelId: number) => apiFetch<CrmModelData>(`/api/crm-import/models/${modelId}`);
/** Исключить модель из импорта топа (повтор безопасен) и вернуть обратно. */
export const excludeCrmModel = (modelId: number) =>
  post<{ model_id: number; excluded: boolean }>(`/api/crm-import/models/${modelId}/exclusion`, {});
export const restoreCrmModel = (modelId: number) =>
  apiFetch<{ model_id: number; excluded: boolean }>(`/api/crm-import/models/${modelId}/exclusion`, {
    method: "DELETE",
  });
/** `dress_id` null — новая папка (или единственная привязанная; две папки — 409 `CHOOSE_DRESS`). */
export const runCrmImport = (
  modelId: number,
  body: { dress_id: number | null; import_ids: number[]; exclude_ids: number[] },
) => post<CrmImportResult>(`/api/crm-import/models/${modelId}`, body);

export const dressTitle = (d: { id: number; name: string | null }): string => d.name ?? `Платье #${d.id}`;

/** «Петрова Татьяна» → «Петрова»: в CRM имя пишется фамилией вперёд. */
export const surname = (name: string): string => name.split(" ")[0] ?? name;
